import { useMemo } from 'react';
import {
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	toProjectType,
	type ProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useJsmPremiumPillExperiment } from '@atlassian/jira-edition-awareness-dropdown/src/common/utils.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import {
	getEditionForProject,
	type ApplicationEdition,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useSiteHasOnlyJira } from '@atlassian/jira-billing/src/controllers/explicitly-licensed-products/use-site-has-only-jira.tsx';
import type { Product, Edition } from '@atlassian/edition-awareness';
import { getApplicationKey, getEaEdition, getEaProduct } from '../ui/utils.tsx';
import type { SupportedApplicationKey } from '../common/types.tsx';

type ProductAndEditionInfo = {
	product: Product | undefined;
	edition: Edition | undefined;
	buttonProps:
		| {
				projectType: ProjectType;
				applicationEdition: ApplicationEdition;
				siteHasJswOnly: boolean;
				applicationKey: SupportedApplicationKey;
		  }
		| undefined;
};

/**
 * Condenses some logic from ProductAndEditionChecker. This is to be used
 * during the integration of Jira with the AudienceAndTiming code in Platform.
 * This will eventually be cleaned up as more EA code is shifted to Platform
 * @returns { product, edition, buttonProps }
 */
export const useProductAndEdition = (): ProductAndEditionInfo => {
	const appEditions = useAppEditions();
	const { loading, data: project } = useProject();
	const siteHasOnlyJira = useSiteHasOnlyJira();

	const { getIsEligibleForJsmPremiumPillExperiment } = useJsmPremiumPillExperiment();
	// Show premium pill button for JSM premium trial experiment
	const projectType = useMemo(() => {
		if (loading) return;

		const type = toProjectType(project?.type);

		if (type) return type;

		if (siteHasOnlyJira) return SOFTWARE_PROJECT;

		if (getIsEligibleForJsmPremiumPillExperiment()) {
			// If the user is on the JSM assets page, we want to default the project type to JSM
			return SERVICE_DESK_PROJECT;
		}
	}, [loading, project?.type, siteHasOnlyJira, getIsEligibleForJsmPremiumPillExperiment]);

	const applicationEdition = useMemo(() => {
		if (!projectType) return;

		if (siteHasOnlyJira) return getEditionForProject(SOFTWARE_PROJECT, appEditions);

		return getEditionForProject(projectType, appEditions);
	}, [appEditions, projectType, siteHasOnlyJira]);

	const { product, edition } = useMemo(() => {
		if (!projectType || !applicationEdition) {
			// We don't have a product or edition, so return nothing
			return {};
		}

		if (projectType === CORE_PROJECT && !siteHasOnlyJira) {
			// We are supporting only JWM implicit licensing when the parent license is JSW
			// Do not show on core products if site does not have only Jira
			return {};
		}
		return {
			product: getEaProduct(projectType),
			edition: getEaEdition(applicationEdition),
		};
	}, [applicationEdition, projectType, siteHasOnlyJira]);

	const buttonProps = useMemo(() => {
		// this code is to be cleaned up when EA content is moved to platform
		if (!product || !edition || !projectType || !applicationEdition) return;

		const applicationKey = getApplicationKey(product);
		if (!applicationKey) return;
		return {
			projectType,
			applicationEdition,
			siteHasJswOnly: siteHasOnlyJira,
			applicationKey,
		};
	}, [edition, applicationEdition, product, projectType, siteHasOnlyJira]);

	return {
		product,
		edition,
		buttonProps,
	};
};
